// client/src/pages/wholesaler/Dashboard.js
// ================================================================
// WHOLESALEHUB WHOLESALER DASHBOARD — v3
// UPDATED: Added Catalog Discounts quick action (6 buttons, 3x2 grid)
// ================================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { wholesalerAPI } from '../../services/api';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TierUsageCard from '../../components/wholesaler/TierUsageCard';
import { 
  Package, ShoppingCart, TrendingUp, AlertCircle, 
  Clock, CheckCircle, Truck, XCircle, Zap, BarChart3, Percent, Tag
} from 'lucide-react';

const WholesalerDashboard = () => {
  const { t } = useTranslation('wholesaler');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const res = await wholesalerAPI.getDashboard();
        setDashboard(res.data.data || res.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError(err.response?.data?.message || t('dashboard.errorLoading'));
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [t]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center"><LoadingSpinner size="lg" /></main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Navbar />
        <main className="flex-grow flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('dashboard.errorTitle')}</h2>
            <p className="text-red-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              {t('dashboard.retry')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const pendingCount = stats.pendingOrders || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* ── Welcome Header ── */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              {t('dashboard.welcome', { name: dashboard?.wholesaler?.companyName || t('dashboard.defaultName') })}
            </h1>
            <p className="text-gray-600 text-lg">{t('dashboard.subtitle')}</p>
          </div>

          {/* ── Stats Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div onClick={() => navigate('/wholesaler/products')} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalProducts')}</h3>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.totalProducts || 0}</p>
              <p className="text-sm text-gray-500 mt-2">{t('dashboard.inCatalog')}</p>
            </div>

            <div onClick={() => navigate('/wholesaler/orders')} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">{t('dashboard.stats.totalOrders', 'Total Orders')}</h3>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.totalOrders || 0}</p>
              <p className="text-sm text-gray-500 mt-2">{t('dashboard.allTime', 'All time')}</p>
            </div>

            <div onClick={() => navigate('/wholesaler/analytics?period=monthly')} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">{t('dashboard.stats.monthlyRevenue', 'Monthly Revenue')}</h3>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">€{stats.monthlyRevenue?.toFixed(2) || '0.00'}</p>
              <p className="text-sm text-gray-500 mt-2">{t('dashboard.thisMonth', 'This month')}</p>
            </div>
          </div>

          {/* ── Order Status Cards — Colored ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div onClick={() => navigate('/wholesaler/orders?status=pending')} className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-yellow-600" />
                <h3 className="text-xs font-medium text-gray-600">{t('dashboard.pipeline.pending', 'Pending')}</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders || 0}</p>
            </div>
            <div onClick={() => navigate('/wholesaler/orders?status=preparing')} className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                <h3 className="text-xs font-medium text-gray-600">{t('dashboard.pipeline.processing', 'In Progress')}</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgressOrders || 0}</p>
            </div>
            <div onClick={() => navigate('/wholesaler/orders?status=delivered')} className="bg-green-50 border-2 border-green-200 p-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="w-4 h-4 text-green-600" />
                <h3 className="text-xs font-medium text-gray-600">{t('dashboard.pipeline.delivered', 'Delivered')}</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.deliveredOrders || 0}</p>
            </div>
            <div onClick={() => navigate('/wholesaler/orders?status=cancelled')} className="bg-red-50 border-2 border-red-200 p-4 rounded-lg cursor-pointer hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <h3 className="text-xs font-medium text-gray-600">{t('dashboard.pipeline.cancelled', 'Cancelled')}</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelledOrders || 0}</p>
            </div>
          </div>

          {/* ── Tier Usage ── */}
          <div className="mb-8">
            <TierUsageCard />
          </div>

          {/* ── Quick Actions — 6 gradient buttons (3x2 on desktop) ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.quickActions.title')}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/wholesaler/products')}
                className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-5 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 transition-all hover:shadow-lg text-left"
              >
                <Package className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.manageProducts')}</h3>
              </button>

              <button
                onClick={() => navigate('/wholesaler/orders')}
                className="relative bg-gradient-to-br from-purple-500 to-purple-600 text-white p-5 rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 transition-all hover:shadow-lg text-left"
              >
                <ShoppingCart className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.viewOrders')}</h3>
                {pendingCount > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow">
                    {pendingCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate('/wholesaler/flash-offers')}
                className="bg-gradient-to-br from-red-500 to-orange-500 text-white p-5 rounded-lg shadow-md hover:from-red-600 hover:to-orange-600 transition-all hover:shadow-lg text-left"
              >
                <Zap className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.flashOffers', 'Flash Offers')}</h3>
              </button>

              {/* NEW: Catalog Discounts */}
              <button
                onClick={() => navigate('/wholesaler/catalog-discounts')}
                className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white p-5 rounded-lg shadow-md hover:from-teal-600 hover:to-cyan-600 transition-all hover:shadow-lg text-left"
              >
                <Tag className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.catalogDiscounts', 'Catalog Discounts')}</h3>
              </button>

              <button
                onClick={() => navigate('/wholesaler/discounts')}
                className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white p-5 rounded-lg shadow-md hover:from-emerald-600 hover:to-teal-600 transition-all hover:shadow-lg text-left"
              >
                <Percent className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.customerDiscounts', 'Customer Discounts')}</h3>
              </button>

              <button
                onClick={() => navigate('/wholesaler/analytics')}
                className="bg-gradient-to-br from-green-500 to-green-600 text-white p-5 rounded-lg shadow-md hover:from-green-600 hover:to-green-700 transition-all hover:shadow-lg text-left"
              >
                <BarChart3 className="w-6 h-6 mb-2 opacity-90" />
                <h3 className="text-sm font-semibold">{t('dashboard.quickActions.viewAnalytics')}</h3>
              </button>
            </div>
          </div>

          {/* ── Recent Orders ── */}
          {dashboard?.recentOrders && dashboard.recentOrders.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">{t('dashboard.recentOrders.title', 'Recent Orders')}</h2>
                <button onClick={() => navigate('/wholesaler/orders')} className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  {t('dashboard.recentOrders.viewAll', 'View All')} →
                </button>
              </div>
              <div className="space-y-3">
                {dashboard.recentOrders.slice(0, 5).map((order) => {
                  const statusColors = {
                    'pending': 'bg-yellow-100 text-yellow-800',
                    'confirmed': 'bg-blue-100 text-blue-800',
                    'processing': 'bg-purple-100 text-purple-800',
                    'preparing': 'bg-purple-100 text-purple-800',
                    'shipped': 'bg-indigo-100 text-indigo-800',
                    'in_transit': 'bg-indigo-100 text-indigo-800',
                    'delivered': 'bg-green-100 text-green-800',
                    'cancelled': 'bg-red-100 text-red-800'
                  };
                  
                  return (
                    <div 
                      key={order._id}
                      onClick={() => navigate('/wholesaler/orders')}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-200"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-semibold text-gray-900">#{order.orderNumber}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                            {order.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-600 truncate">{order.businessName}</p>
                          <span className="text-xs text-gray-400 hidden sm:inline">•</span>
                          <p className="text-xs text-gray-500 hidden sm:inline">
                            {new Date(order.createdAt).toLocaleDateString('el-GR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="font-bold text-gray-900">€{order.total?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </main>
      </div>
      <Footer />
    </div>
  );
};

export default WholesalerDashboard;